import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import useAdminStore from '../../hooks/useAdmin';
import styles from './Layout.module.css';
import ChangePasswordModal from '../ChangePasswordModal'; // @change-password-import-v2

const NAV = [
  { section: 'Επισκόπηση', items: [
    { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  ]},
  { section: 'Ιατροί', items: [
    { to: '/doctors',         icon: '🩺', label: 'Όλοι οι Ιατροί' },
    { to: '/doctors/pending', icon: '⏳', label: 'Αναμένουν Έγκριση', alert: true },
  ]},
  { section: 'Ασθενείς', items: [
    { to: '/patients', icon: '👥', label: 'Όλοι οι Ασθενείς' },
  ]},
  { section: 'Πρόγραμμα', items: [
    { to: '/rota',          icon: '📅', label: 'Ρόστερ & Ημερολόγιο' },
    /* APPT_CALENDAR_NAV_2026-08-05 */
    { to: '/appointments',  icon: '🗓️', label: 'Ραντεβού' },
    { to: '/slots/pending', icon: '🕐', label: 'Αιτήματα Ρόστερ', alert: true },
  ]},
  { section: 'Οικονομικά', items: [
    { to: '/billing', icon: '💳', label: 'Χρεώσεις & Πληρωμές' },
    // /* @d4-payouts-deprecated */ Deprecated by D4 (/clearings) — page uses stale hardcoded €7.50 and ignores per-doctor commission. Route in App.jsx remains but link removed from sidebar.
    // { to: '/payouts', icon: '💰', label: 'Αμοιβές Ιατρών' },
    { to: '/clearings', icon: '💸', label: 'Εκκαθαρίσεις' }, // /* @d4-clearings-nav */
    { to: '/clearings-run', icon: '🛠️', label: 'Νέα Εκκαθάριση' }, // /* @d4-preview-wiring */
    /* DOCTOR_INVOICES_NAV_2026-07-06 */
    { to: '/doctor-invoices', icon: '💳', label: 'Τιμολόγια Ιατρών' },
    { to: '/discount-codes', icon: '🎟️', label: 'Κωδικοί & Δωρεάν Συνεδρίες' }, // @discount-codes-portal-v1
  ]},
  { section: 'Αναφορές', items: [
    { to: '/analytics', icon: '📈', label: 'Αναλυτικά & Αναφορές' },
    { to: '/attendance', icon: '⏱️', label: 'Προσέλευση Γιατρών' }, // @attendance-portal-v1
    { to: '/renewals', icon: '💊', label: 'Επαναλήψεις Συνταγών' }, // @chronic-renewal-v1
    { to: '/sick-leave', icon: '💊', label: 'Αναρρωτικές Άδειες' }, // @chronic-renewal-v1
    { to: '/blood-tests', icon: '🩸', label: 'Εξετάσεις Αίματος' }, // @blood-tests-v1
    { to: '/prep', icon: '🛡️', label: 'PrEP' }, // @prep-v1
    { to: '/clinic-interest', icon: '🔔', label: 'Ενδιαφέρον για Κλινικές' }, // @clinic-interest-report-v1
  ]},
  { section: 'Ρυθμίσεις', items: [
    { to: '/services', icon: '⚙️', label: 'Τύποι Υπηρεσιών' },
  ]},
  { section: 'Διαχείριση', items: [
    { to: '/admins', icon: '👤', label: 'Διαχειριστές' },
  ]},
];

const PAGE_TITLES = {
  '/dashboard':      'Dashboard',
  '/doctors':        'Όλοι οι Ιατροί',
  '/doctors/pending':'Αναμένουν Έγκριση',
  '/patients':       'Όλοι οι Ασθενείς',
  '/rota':           'Ρόστερ & Ημερολόγιο',
  '/appointments':   'Ραντεβού',
  '/slots/pending':  'Αιτήματα Ρόστερ',
  '/billing':        'Χρεώσεις & Πληρωμές',
  '/payouts':        'Αμοιβές Ιατρών',
  '/clearings':      'Εκκαθαρίσεις', // /* @d4-clearings-title */
  '/clearings-run':  'Νέα Εκκαθάριση', // /* @d4-preview-wiring */
  '/doctor-invoices': 'Τιμολόγια Ιατρών',
  '/discount-codes': 'Κωδικοί & Δωρεάν Συνεδρίες', // @discount-codes-portal-v1
  '/analytics':      'Αναλυτικά & Αναφορές',
  '/attendance':     'Προσέλευση Γιατρών', // @attendance-portal-v1
  '/renewals':       'Επαναλήψεις Συνταγών', // @chronic-renewal-v1
  '/clinic-interest': 'Ενδιαφέρον για Κλινικές', // @clinic-interest-report-v1
  '/services':       'Τύποι Υπηρεσιών',
  '/admins':         'Διαχειριστές',
};

export default function AdminLayout({ children, alerts = {} }) {
  const { admin, logout } = useAdminStore();
  const [showChangePassword, setShowChangePassword] = React.useState(false); // @change-password-state-v2
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = PAGE_TITLES[location.pathname] || 'Admin';

  const handleLogout = async () => {
    if (window.confirm('Αποσύνδεση από την πύλη διαχείρισης;')) {
      await logout();
      navigate('/login');
    }
  };

  return (
    <div className={styles.shell}>
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          <div className={styles.logoCross} />
          <div className={styles.logoText}>Doc<span>Now</span></div>
        </div>

        <div
          className={styles.adminBadge}
          role="button"
          tabIndex={0}
          onClick={() => setShowChangePassword(true)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowChangePassword(true); }}
          style={{ cursor: "pointer" }}
          title="Αλλαγή κωδικού"
          data-sentinel="@change-password-badge-v2"
        >
          <div className={styles.badgeRole}>⚙️ Admin Portal</div>
          <div className={styles.badgeName}>
            {admin?.firstName ? `${admin.firstName} ${admin.lastName}` : 'Administrator'}
          </div>
        </div>

        <nav className={styles.nav}>
          {NAV.map(({ section, items }) => (
            <div key={section}>
              <div className={styles.navSection}>{section}</div>
              {items.map(({ to, icon, label, alert }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `${styles.navItem} ${isActive ? styles.active : ''}`
                  }
                >
                  <span className={styles.navIcon}>{icon}</span>
                  <span className={styles.navLabel}>{label}</span>
                  {alert && alerts[to] > 0 && (
                    <span className={styles.navBadge}>{alerts[to]}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <button className={styles.navItem} onClick={handleLogout}>
            <span className={styles.navIcon}>🚪</span>
            <span className={styles.navLabel}>Αποσύνδεση</span>
          </button>
        </div>
      </aside>
      {/* @change-password-modal-v2 */}
      <ChangePasswordModal
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      <div className={styles.main}>
        <header className={styles.topbar}>
          <h1 className={styles.pageTitle}>{pageTitle}</h1>
          <div className={styles.topbarDate}>
            {new Date().toLocaleDateString('el-GR', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
            })}
          </div>
          <div className={styles.topbarAlert} onClick={() => navigate('/slots/pending')}>
            🔔 {(alerts['/doctors/pending'] || 0) + (alerts['/slots/pending'] || 0)} ειδοποιήσεις
          </div>
        </header>

        <main className={styles.content}>
          {children}
        </main>
      </div>
    </div>
  );
}
